/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useMemo, useRef } from 'react';

import { useAppSelector } from '../../../app/hooks';
import { selectFhirMedicines } from '../Fhir/fhirSlice';
import { formatDate } from '../utils';
import styles from './CurrentMedicinesContainer.module.scss';
import FHIRAccordion from '../Fhir/FHIRAccordion';
import { PatientMedication } from '../Fhir/fhir_types';
import { DocumentVersionType, fhirPatientDataTypes } from '../algorithm/types';

export function CurrentMedicinesContainer({
  documentVersion,
  medicines: medicinesOverride,
}: {
  documentVersion?: DocumentVersionType;
  medicines?: PatientMedication[];
}) {
  const divRef = useRef<HTMLDivElement>(null);
  const storedMedicines = useAppSelector(selectFhirMedicines);
  const medicines = medicinesOverride ?? storedMedicines;

  const medicinesSorted = useMemo(() => {
    return medicines
      ? [...medicines].sort((a, b) => {
          const authoredA = a.resourceType === 'MedicationRequest' ? a.authoredOn : a.dateAsserted;
          const authoredB = b.resourceType === 'MedicationRequest' ? b.authoredOn : b.dateAsserted;
          const dateA = authoredA ? new Date(authoredA).getTime() : 0;
          const dateB = authoredB ? new Date(authoredB).getTime() : 0;
          return dateB - dateA; // Sort in descending order (most recent first)
        })
      : [];
  }, [medicines]);

  if (!medicines || medicines.length === 0) {
    return null;
  }

  const fhirDataType = fhirPatientDataTypes.find((x) => x.id === 'Medication');

  return (
    <FHIRAccordion
      heading={fhirDataType?.value}
      divRef={divRef}
      defaultCollapsed={true}
      mixpanelWidgetName="medication"
      documentVersion={documentVersion}
      style={{ minWidth: 110 }}
    >
      <div ref={divRef} className={styles.medicineWidgets}>
        {medicinesSorted.map((res, index) => (
          <MedicineWidget res={res} key={index} />
        ))}
      </div>
    </FHIRAccordion>
  );
}

export function MedicineWidget({ res }: { res: PatientMedication }) {
  if (res.status !== 'active') {
    return null;
  }
  const medicationName =
    res.medicationReference?.display ||
    res.medicationCodeableConcept?.text ||
    res.medicationCodeableConcept?.coding?.[0]?.display ||
    res.medicationCodeableConcept?.coding?.[0]?.code;
  const nameOther = medicationName?.split('(', 2);
  const nameOnly = nameOther?.[0];
  const dosage = res.resourceType === 'MedicationStatement' ? res.dosage : res.dosageInstruction;
  const authoredOn = res.resourceType === 'MedicationStatement' ? res.dateAsserted : res.authoredOn;
  const clinicianName =
    res.resourceType === 'MedicationStatement'
      ? res.informationSource?.display
      : res.requester?.display || res.recorder?.display;
  return (
    <div key={res.id} className={styles.medicineWidget}>
      <h2>
        <span className={styles.nameOnly}>{nameOnly}</span>
        {nameOther?.[1] ? <span className={styles.nameOther}>({nameOther[1]}</span> : ''}
        {res.resourceType === 'MedicationRequest' && res.doNotPerform ? ' - Do not perform' : ''}
      </h2>
      {dosage?.map((i, index) => (
        <div className={styles.innerContainer} key={index}>
          {i.text?.trim()}{' '}
          {i.doseAndRate?.[0]?.doseQuantity?.value
            ? `(${i.doseAndRate?.[0]?.doseQuantity?.value}\u00A0${i.doseAndRate?.[0]?.doseQuantity?.unit})`
            : ''}
        </div>
      ))}
      {(authoredOn || clinicianName) && (
        <div className={styles.finalLine}>
          {authoredOn && <div className={styles.date}>{formatDate(authoredOn)}</div>}
          {clinicianName && <div className={styles.doctorName}>{clinicianName}</div>}
        </div>
      )}
    </div>
  );
}
